import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    Customer,
    Invoice,
    InvoiceConfig,
    InvoiceLine,
    PriorRetention,
    TaxCategoryCode,
} from '../../types';
import { formatCents, formatTaxRateBp } from '../money';
import {
    CONTRIBUTES_TO_NET,
    OUR_REFERENCE_LABEL,
    costCenterLine,
    effectiveRetentionPercent,
    groupAdvanceDeductions,
    invoiceHeading,
    invoiceOurReference,
    invoiceProjectRefs,
    lineDiscountNote,
    performancePeriod,
    presentInvoiceLines,
    invoiceAmountCents,
    printedServicePeriod,
    retentionNote,
    splitShareNote,
} from '../invoiceLogic';
import {
    advanceDeductionHeadingLabel,
    advanceDeductionLineLabel,
    advanceDeductionRangeLabel,
    advanceDeductionTotal,
    advanceNumberingPlan,
    payableBreakdown,
} from '../advanceInvoiceLogic';

import { COLORS, formatDate } from './pdfUtils';
import {
    A4_GEOMETRY,
    CompanyInfo,
    cityFromAddress,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    fitPosColumn,
    longDate,
} from './letterhead';
import { ensureEmbeddedFont, registerEmbeddedFont } from './embeddedFont';
import { appendPdf } from './mergePdf';

/**
 * Rechnungs-PDF.
 *
 * Nutzt dieselbe Briefkopf- und Briefpapier-Mechanik wie das Angebot
 * (letterhead.ts). "Gleiches Briefpapier wie Angebote" ist damit keine Frage
 * der Pflege zweier Layouts, sondern folgt aus der geteilten Implementierung.
 */

export interface InvoicePdfInput {
    invoice: Invoice;
    /** Ab ISSUED der eingefrorene customerSnapshot, davor der Live-Kunde. */
    customer: Customer;
    company: CompanyInfo;
    config: InvoiceConfig;
    userName?: string;
    userSignature?: string;
}

/**
 * Menge fuer die Positionstabelle.
 *
 * Bewusst NICHT ueber den Geld-Formatierer: der erzwingt zwei Nachkommastellen,
 * Mengen duerfen aber drei haben. Eine Menge von 10,005 wuerde als "10,01"
 * erscheinen, waehrend der Zeilenbetrag aus 10,005 gerechnet ist — die Rechnung
 * haette dann eine Zeile, die sich nicht nachrechnen laesst, und nach der
 * Festschreibung waere das nicht mehr zu korrigieren.
 */
const fmtQuantity = (q: number): string =>
    q.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

/**
 * Feste Spaltenbreiten der Positionstabelle in mm; die Leistungsspalte fuellt
 * den Rest. Exportiert, damit ein Test mit den echten Schriftmetriken pruefen
 * kann, dass die Inhalte hineinpassen — vier von Hand gesetzte Breiten sind
 * sonst eine Behauptung, die erst beim Kunden auffaellt.
 */
export const INVOICE_TABLE = {
    fontSize: 8.5,
    /** Innenabstand links und rechts je Zelle. */
    paddingX: 1.5,
    /**
     * `pos` ist die MINDESTbreite: die Pos.-Spalte waechst mit einer langen
     * LV-Nummer mit (siehe invoicePosColumn). Die uebrigen vier sind fest.
     */
    widths: { pos: 10, quantity: 24, unitPrice: 24, tax: 14, net: 26 },
    /**
     * Untergrenze der Leistungsspalte in mm. Darunter bricht schon eine
     * mittellange Leistungsbezeichnung dreizeilig um, und die Tabelle wird zur
     * Textwueste. Aus ihr folgt, wieviel die Pos.-Spalte hoechstens nehmen
     * darf — kein zweiter, davon unabhaengiger Zahlenwert.
     */
    titleMinWidth: 60,
} as const;

/**
 * Breite und Schriftgroesse der Pos.-Spalte fuer die tatsaechlich vorkommenden
 * Positionsnummern.
 *
 * Anlass: die Positionsnummer der Rechnung ist im Regelfall fortlaufend ("1",
 * "2", …), kann aber von Hand mit der LV-Nummer des Auftraggebers ueberschrieben
 * werden (InvoiceEditor, `posNumberManual`). "1.1.10." braucht 9,1 mm und
 * passte nicht in die 7 mm Textbreite der 10-mm-Spalte — auf dem Beleg stand
 * "1.1.1" und darunter "0.".
 *
 * Exportiert, damit ein Test die Schranken mit den echten Schriftmetriken
 * nachrechnen kann.
 */
export const invoicePosColumn = (
    positions: readonly (string | null | undefined)[],
    measure: (text: string) => number,
): { width: number; fontSize: number } =>
    fitPosColumn(positions, measure, {
        min: INVOICE_TABLE.widths.pos,
        // Was die Leistungsspalte uebrig laesst: Satzspiegel minus der vier
        // festen Zahlenspalten minus ihrer Untergrenze.
        max:
            A4_GEOMETRY.contentWidth -
            (INVOICE_TABLE.widths.quantity +
                INVOICE_TABLE.widths.unitPrice +
                INVOICE_TABLE.widths.tax +
                INVOICE_TABLE.widths.net) -
            INVOICE_TABLE.titleMinWidth,
        paddingX: INVOICE_TABLE.paddingX,
        fontSize: INVOICE_TABLE.fontSize,
    });

/**
 * Luft zwischen dem Belegkopf (Ueberschrift, Projekt, Kostenstelle, Zusatztext,
 * Grund) und dem Anschreiben, in mm.
 *
 * Zwei Zeilenhoehen des rechten Kopfblocks (2 × 4.6). Der Abstand muss groesser
 * sein als der zwischen zwei Kopfzeilen, sonst liest sich die erste
 * Anschreibenzeile als weitere Kopfangabe — genau das war die Beanstandung am
 * Andruck.
 */
export const INTRO_GAP = 9.2;

/**
 * Linke Schranke der Beschriftungskante im rechten Kopfblock, in mm.
 *
 * Das Anschriftfeld ist 85 mm breit; weiter nach links darf der Block nicht
 * ausweichen, sonst laeuft er hinein. Modulweit, weil die Schranke schon beim
 * Umbruch langer Werte gebraucht wird — also bevor der Block gezeichnet wird.
 */
const META_LABEL_MIN_X = A4_GEOMETRY.margin + 90;

/**
 * Zeilenhoehe INNERHALB des Anschreibens, in mm.
 *
 * Eine Rasterstufe unter den 5 mm der Kopfzeilen und damit gleich der
 * Zeilenhoehe der Zahlungsbedingungen weiter unten: das Anschreiben ist
 * Fliesstext und soll als ein Block stehen, nicht als Folge einzelner
 * Angaben. Der Abstand DAVOR (INTRO_GAP) traegt die Trennung, nicht der
 * Zeilenabstand.
 */
export const INTRO_LINE_HEIGHT = 4.5;

/** Nutzbare Textbreite einer Spalte: Spaltenbreite abzueglich beider Innenabstaende. */
export const invoiceTableTextWidth = (column: keyof typeof INVOICE_TABLE.widths): number =>
    INVOICE_TABLE.widths[column] - 2 * INVOICE_TABLE.paddingX;

/**
 * Beschriftung der Summenzeile bei Steuersatz 0, je Steuerkategorie.
 *
 * EN16931 unterscheidet die Faelle ueber BT-118; auf dem Papier muessen sie
 * ebenso auseinandergehalten werden. "Steuerfrei" fuer alles waere bei AE
 * (Steuerschuldnerschaft des Leistungsempfaengers) sachlich falsch: der Umsatz
 * ist steuerpflichtig, die Steuer schuldet nur ein anderer.
 */
const ZERO_RATE_LABEL: Partial<Record<TaxCategoryCode, string>> = {
    AE: 'Steuerschuldnerschaft des Leistungsempfängers',
    E: 'Steuerfrei',
    Z: '0 % USt',
    K: 'Steuerfreie innergemeinschaftliche Lieferung',
    G: 'Steuerfreie Ausfuhrlieferung',
    O: 'Nicht steuerbar',
    S: 'Ohne Umsatzsteuer',
};

export const generateInvoicePDF = (
    input: InvoicePdfInput,
    opts?: {
        __docFactory?: () => jsPDF;
        /**
         * Vorhergesagte Zuordnung fuer den Leistungszeitraum, wenn der Beleg
         * selbst noch keine traegt.
         *
         * Ausschliesslich fuer die Live-Vorschau des Editors: ein ENTWURF
         * traegt noch keine Zuordnung — die entsteht erst beim Ausstellen.
         * Ohne diese Naht zeigt die Maske den Zeitraum an, die Vorschau daneben
         * laesst ihn weg, und der Bearbeiter haelt das fuer einen Fehler des
         * Belegs.
         *
         * Sie steht in der Kette von performancePeriod HINTER der echten
         * Zuordnung: sobald der Beleg zugeordnete Zeilen hat, gewinnt immer
         * diese. Der Ausstellungspfad (renderInvoicePdfBytes) setzt sie nicht —
         * was archiviert wird, kommt allein vom Beleg.
         */
        billingPeriodFallback?: { from: string; to: string };
    },
): jsPDF => {
    const { invoice, customer, company, config } = input;
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;

    const doc = opts?.__docFactory
        ? opts.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', compress: true });

    // Eingebettete Schrift unter dem Namen "helvetica" registrieren, bevor
    // irgendetwas gezeichnet wird. Danach benutzt jeder setFont-Aufruf in
    // diesem Generator UND im gemeinsamen Briefpapier automatisch die
    // eingebettete Variante — Voraussetzung fuer PDF/A und damit fuer ZUGFeRD.
    //
    // Sind die Schriftdaten nicht geladen, wird still mit den Standardschriften
    // gerendert: das Dokument ist dann kein PDF/A, aber es entsteht. Der
    // Aufrufer erfaehrt es ueber isEmbeddedFontLoaded.
    registerEmbeddedFont(doc);

    const [tp0, tp1, tp2] = COLORS.text.primary;
    // BEWUSST primary, nicht secondary: die Rechnung steht komplett in EINEM
    // Schwarz — drei Graustufen auf einem Beleg lasen sich unruhig. Der Alias
    // bleibt, damit die Zeichenstellen nicht alle angefasst werden mussten.
    const [ts0, ts1, ts2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();

    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    // ── Anschriftfeld ─────────────────────────────────────────────────────
    // Name in derselben Groesse wie Strasse und Ort: das Anschriftfeld ist
    // EIN Block, keine Ueberschrift mit Unterzeilen. Zeilen 2/3 kommen aus dem
    // Kundenstamm (name2/name3).
    let addrY = addressFieldY + 5;
    doc.setTextColor(tp0, tp1, tp2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const nameLines = [customer.name, customer.name2, customer.name3]
        .map((n) => n?.trim())
        .filter((n): n is string => !!n)
        .flatMap((n) => doc.splitTextToSize(n, 85) as string[]);
    doc.text(nameLines, margin, addrY);
    addrY += nameLines.length * 5;
    const recipientLines: string[] = [];
    if (customer.street || customer.zip || customer.city) {
        if (customer.street) recipientLines.push(customer.street);
        const cityLine = [customer.zip, customer.city].filter(Boolean).join(' ').trim();
        if (cityLine) recipientLines.push(cityLine);
    } else if ((customer as { address?: string }).address?.trim()) {
        recipientLines.push(
            ...(customer as { address: string }).address.replace(/,\s*/g, '\n').split('\n'),
        );
    }
    // Zusatzzeilen des Belegs (details.addressExtra1..3) direkt unter der
    // Adresse — Angaben, die der Auftraggeber im Anschriftfeld verlangt.
    for (const extra of [
        invoice.details?.addressExtra1,
        invoice.details?.addressExtra2,
        invoice.details?.addressExtra3,
    ]) {
        const text = extra?.trim();
        if (text) recipientLines.push(...(doc.splitTextToSize(text, 85) as string[]));
    }
    recipientLines.forEach((l) => {
        doc.text(l, margin, addrY);
        addrY += 5;
    });

    // ── Rechter Block: die Pflichtangaben zum Beleg ───────────────────────
    // Bewusst rechts und tabellarisch: Rechnungsnummer, Datum und
    // Leistungszeitraum sind die Angaben, die eine Buchhaltung zuerst sucht.
    //
    // Die ersten drei Zeilen stehen FEST in dieser Folge und werden hier
    // zusammen aufgebaut, damit sie niemand einzeln verschiebt:
    //
    //   Rechnungsdatum → Rechnungs-Nr. → Kunden-Nr.
    //
    // Erst wann, dann welcher Beleg, dann wessen Konto — so liest der Betreiber
    // seinen Beleg, und so gibt er ihn am Telefon durch. Alles Weitere haengt
    // an Bedingungen und schliesst sich unten an; keine dieser bedingten Zeilen
    // darf zwischen die drei geraten. Die Nummer bleibt dabei eine Zeile des
    // Blocks wie jede andere: gleiche Beschriftungskante, gleiche Schrift.
    //
    // Die Nummernzeile steht IMMER, auch beim Entwurf — dort mit '(Entwurf)'
    // als Wert. Sie ist die einzige Stelle, an der die Belegnummer erscheint
    // (die Ueberschrift nennt seit Neuem nur noch die Belegart), und eine
    // Angabe, die mal da ist und mal nicht, sucht der Leser beim naechsten
    // Beleg an einer Stelle, an der sie diesmal fehlt. Der Entwurf sagt so
    // ausdruecklich, dass die Nummer noch aussteht, statt zu schweigen.
    const metaRows: Array<[string, string]> = [];
    metaRows.push(['Rechnungsdatum', formatDate(invoice.invoiceDate)]);
    metaRows.push(['Rechnungs-Nr.', invoice.invoiceNumber?.trim() || '(Entwurf)']);
    if (customer.customerNumber?.trim()) {
        metaRows.push(['Kunden-Nr.', customer.customerNumber.trim()]);
    }
    // Der ProjektNAME steht bewusst NICHT hier, sondern als eigene Zeile unter
    // der Ueberschrift: die Spalte hat rund 45 mm, ein Projektname braucht mehr
    // und wurde vorher ueber drei Zeilen umbrochen. Die NUMMER dagegen gehoert
    // genau hierher — als "Unser Zeichen" direkt hinter die beiden anderen
    // Schluessel des Vorgangs (Beleg-Nr., Kunden-Nr.) und vor alles, was
    // Zeitraum oder Fremdreferenz ist. Doppelt genannt wird nichts: oben der
    // Name, hier die Nummer.
    const ourReference = invoiceOurReference(invoiceProjectRefs(invoice));
    if (ourReference) {
        // Eine Sammelrechnung ueber viele Projekte traegt eine Liste von
        // Nummern, die breiter werden kann als die Spalte. Sie bricht deshalb
        // auf Folgezeilen ohne Beschriftung um, statt in die Beschriftung zu
        // laufen — der Umbruch wird gegen die SCHMALSTE moegliche Spalte
        // gerechnet (linke Schranke bis rechter Rand), damit er auch dann noch
        // passt, wenn eine andere Zeile den Block breit macht.
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const labelWidth = doc.getTextWidth(OUR_REFERENCE_LABEL);
        doc.setFont('helvetica', 'bold');
        const valueWidth = Math.max(rightX - META_LABEL_MIN_X - labelWidth - 4, 20);
        const chunks = doc.splitTextToSize(ourReference, valueWidth) as string[];
        chunks.forEach((chunk, i) => {
            metaRows.push([i === 0 ? OUR_REFERENCE_LABEL : '', chunk]);
        });
    }
    // Zwei Zeitraeume, zwei Fragen — und sie duerfen nicht verwechselt werden:
    //
    //   Abrechnungszeitraum  WORUEBER wird abgerechnet? Die Angabe des
    //                        Betriebs aus dem Anlagedialog, in der Maske
    //                        korrigierbar (invoiceLogic: printedServicePeriod).
    //   Leistungszeitraum    WANN wurde geleistet? Erste bis letzte Leistung,
    //                        die dieser Beleg abrechnet — der Leistungszeitpunkt
    //                        nach §14 Abs. 4 Nr. 6 UStG (performancePeriod).
    //
    // Kumulativ fallen sie auseinander: eine Rechnung ueber Juni/Juli rechnet
    // Leistungen ab dem 05.03. mit ab, weil sie den Gesamtstand fortschreibt.
    // Beide Angaben gehoeren deshalb auf den Beleg; nur eine davon zu drucken
    // hiesse, die andere Frage unbeantwortet zu lassen.
    const statedPeriod = printedServicePeriod(invoice);
    const servicePeriod = performancePeriod(invoice, opts?.billingPeriodFallback);
    const rangeText = (p: { from?: string; to?: string }): string | undefined =>
        p.from && p.to
            ? p.from === p.to
                ? formatDate(p.to)
                : `${formatDate(p.from)} – ${formatDate(p.to)}`
            : undefined;
    const statedText = rangeText(statedPeriod);
    const serviceText = rangeText(servicePeriod);

    // Ausgelassen wird der Abrechnungszeitraum nur, wenn er WORTGLEICH dasselbe
    // sagt wie der Leistungszeitraum darunter. Zwei identische Zeitraeume
    // untereinander sind keine Auskunft mehr, sondern lassen den Leser nach dem
    // Unterschied suchen, den es nicht gibt. Stehen bleibt dann der
    // Leistungszeitraum — er ist die Pflichtangabe.
    if (statedText && statedText !== serviceText) {
        metaRows.push(['Abrechnungszeitraum', statedText]);
    }
    if (serviceText) {
        metaRows.push(['Leistungszeitraum', serviceText]);
    } else if (servicePeriod.to) {
        metaRows.push(['Leistungsdatum', formatDate(servicePeriod.to)]);
    } else if (servicePeriod.from) {
        // Angefangener Zeitraum ohne Ende. Die Validierung laesst das nicht bis
        // zur Ausstellung durch, in der Entwurfsvorschau soll die Angabe aber
        // sichtbar sein statt kommentarlos zu fehlen.
        metaRows.push(['Leistung ab', formatDate(servicePeriod.from)]);
    }
    // Kein Faelligkeitsdatum auf einem Beleg, der Geld zurueckgibt: es gibt
    // nichts zu bezahlen, und die Angabe liest sich als Zahlungsfrist.
    if (invoice.dueDate && invoice.totals.payableCents >= 0) {
        metaRows.push(['Fällig am', formatDate(invoice.dueDate)]);
    }
    if (invoice.details?.buyerReference?.trim()) {
        metaRows.push(['Leitweg-ID', invoice.details.buyerReference.trim()]);
    }
    if (invoice.details?.orderReference?.trim()) {
        metaRows.push(['Bestell-Nr.', invoice.details.orderReference.trim()]);
    }
    if (invoice.details?.contractReference?.trim()) {
        metaRows.push(['Vertrag', invoice.details.contractReference.trim()]);
    }
    // Bei der Gutschrift im umsatzsteuerlichen Sinn vergibt der
    // Leistungsempfaenger die Nummer (§ 14 Abs. 4 Nr. 4 UStG). Ohne sie fehlt
    // dem Beleg eine Pflichtangabe — unsere eigene Nummer ersetzt sie nicht.
    if (invoice.details?.externalDocumentNumber?.trim()) {
        metaRows.push(['Beleg-Nr. Kunde', invoice.details.externalDocumentNumber.trim()]);
    }

    // Der Block beginnt unterhalb der Kopf-Infospalte des Briefpapiers
    // (Telefon/Mail/USt-IdNr.). Ohne diese Schranke schreibt die
    // Rechnungsnummer in die letzte Zeile der Infospalte hinein — die
    // Infospalte ist beliebig lang, ihre Unterkante steht erst nach dem
    // Zeichnen fest, und genau die liefert rightColBottom.
    let metaY = Math.max(addressFieldY + 5, rightColBottom + 6);
    const metaLabelDefaultX = rightX - 60;
    // Linke Schranke: das Anschriftfeld ist 85 mm breit, der Metablock darf
    // nicht hineinlaufen.
    const metaLabelMinX = META_LABEL_MIN_X;
    doc.setFontSize(9);

    // EINE Spaltenkante fuer alle Zeilen, bestimmt von der breitesten
    // Kombination aus Beschriftung und Wert.
    //
    // Zeilenweise auszuweichen — was hier vorher stand — loest zwar die
    // Ueberlappung von "Leistungszeitraum" mit seinem eigenen Datum, rueckt
    // aber genau diese eine Beschriftung nach links und laesst die Spalte
    // ausgefranst zurueck. Der Block ist eine Tabelle, und eine Tabelle hat
    // eine Kante.
    const neededWidth = metaRows.reduce((widest, [label, value]) => {
        doc.setFont('helvetica', 'normal');
        const labelWidth = doc.getTextWidth(label);
        doc.setFont('helvetica', 'bold');
        const valueWidth = doc.getTextWidth(value);
        return Math.max(widest, labelWidth + valueWidth + 4);
    }, 0);
    const metaLabelX = Math.max(metaLabelMinX, Math.min(metaLabelDefaultX, rightX - neededWidth));

    for (const [label, value] of metaRows) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(ts0, ts1, ts2);
        doc.text(label, metaLabelX, metaY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(value, rightX, metaY, { align: 'right' });
        metaY += 4.6;
    }

    // ── Ort und Datum ─────────────────────────────────────────────────────
    const companyCity = cityFromAddress(company.address);
    const dateLine = companyCity
        ? `${companyCity}, ${longDate(invoice.invoiceDate)}`
        : longDate(invoice.invoiceDate);

    let y = Math.max(addrY + 6, metaY + 6, addressFieldY + 30, rightColBottom + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11.5);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(dateLine, rightX, y, { align: 'right' });
    y += 12;

    const ensureSpace = createSpaceGuard(
        lh,
        geo,
        () => y,
        (v) => {
            y = v;
        },
    );

    // ── Betreff ───────────────────────────────────────────────────────────
    // Ueberschrift und Projektzeile kommen aus invoiceHeading — welche Angabe
    // oben steht, ist eine Regel und keine Layoutfrage; hier faellt nur noch
    // die Entscheidung ueber Groesse und Farbe.
    //
    // GENAU EINE Titelzeile. Frueher stand darunter noch ein dezenter
    // Belegartvermerk, sobald eine eigene Ueberschrift den Standardtitel
    // verdraengt hatte — auf dem Blatt las sich das als zweite, widersprechende
    // Ueberschrift ("Schlussrechnung" / "Rechnung (Entwurf)"). Die Belegart
    // steht im rechten Kopfblock an der Nummernzeile, den Entwurfsvermerk
    // fuehrt invoiceHeading am Titel selbst mit.
    const heading = invoiceHeading(invoice, undefined, {
        defaultHeaderText: config.defaultHeaderText,
    });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    const titleLines = doc.splitTextToSize(heading.title, contentWidth) as string[];
    for (const l of titleLines) {
        doc.text(l, margin, y);
        y += 6;
    }
    y += 2;

    // Projekt, Kostenstelle und Zusatztext — EIN Block, in dieser Reihenfolge.
    //
    // Schriftbild der Projektzeile Zeichen fuer Zeichen wie im Angebot
    // (generatorQuote.ts: 'Projekt: <Text>', helvetica normal 10.5, volle
    // Primaerfarbe, Zeilenhoehe 5.5, danach 3 mm Luft). Kunde und Buchhaltung
    // legen Angebot und Rechnung nebeneinander; zwei verschiedene Schriftbilder
    // fuer dieselbe Angabe lesen sich wie zwei verschiedene Vorgaenge.
    //
    // Ueber die volle Inhaltsbreite statt im rechten Block: die Buchhaltung des
    // Auftraggebers ordnet den Beleg ueber das Projekt zu, und ein Projektname
    // wie "Neubau Verwaltungsgebaeude Bauabschnitt 2" braucht mehr Platz, als
    // die 60-mm-Spalte je hergeben wird. Quelle ist ausschliesslich der Beleg
    // selbst — beim Ausstellen eingefroren, das Andruckbild damit
    // unveraenderlich. Ein Altbeleg ohne Projektvermerk druckt die Zeile gar
    // nicht, statt etwas zu raten. Die Projektzeile nennt bewusst NUR den
    // Namen: die Nummer ist unser interner Schluessel (invoiceHeading). Welcher
    // Name das ist, entscheidet ebenfalls invoiceHeading — der Beleg darf einen
    // eigenen Anzeigenamen tragen (details.projectNameOverride), ohne dass das
    // Projekt selbst umbenannt wird.
    //
    // Kostenstelle und Zusatztext haengen ohne Zwischenluft an der
    // Projektzeile, weil sie dieselbe Frage beantworten — worauf der
    // Empfaenger den Beleg bucht. Die 3 mm Luft schliessen den ganzen Block ab
    // und nicht jede Angabe einzeln; leere Felder erscheinen gar nicht.
    //
    // Wie die Kostenstellenzeile lautet, entscheidet costCenterLine: der
    // Auftraggeber nennt die Angabe "Kostenstelle", "PSP-Element" oder
    // "Innenauftrag", und das steht am Beleg (details.costCenterLabel). Der
    // Generator setzt hier nur, was ihm gereicht wird.
    const costCenter = costCenterLine(invoice.details);
    const subjectNote = invoice.details?.subjectNote?.trim();
    if (heading.projectLine || costCenter || subjectNote) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(tp0, tp1, tp2);
        if (heading.projectLine) {
            doc.setFontSize(10.5);
            const projectLines = doc.splitTextToSize(
                `Projekt: ${heading.projectLine}`,
                contentWidth,
            ) as string[];
            for (const l of projectLines) {
                ensureSpace(25);
                doc.text(l, margin, y);
                y += 5.5;
            }
        }
        if (costCenter) {
            doc.setFontSize(10.5);
            for (const l of doc.splitTextToSize(costCenter, contentWidth) as string[]) {
                ensureSpace(25);
                doc.text(l, margin, y);
                y += 5.5;
            }
        }
        // Freitext im Textgrad des Dokuments (10), nicht im Grad der
        // Projektzeile: er ist eine Mitteilung und keine Kopfangabe, und
        // mehrere Zeilen in 10.5 draengten sich vor die Anrede.
        if (subjectNote) {
            doc.setFontSize(10);
            // splitTextToSize bricht an \n mit — ein mehrzeilig eingegebener
            // Text behaelt seine Absaetze.
            for (const l of doc.splitTextToSize(subjectNote, contentWidth) as string[]) {
                ensureSpace(25);
                doc.text(l, margin, y);
                y += 5;
            }
        }
        y += 3;
    }

    // Grund bei Storno, Korrektur und Gutschrift — Pflichtangabe des Belegs.
    if (invoice.details?.reasonText?.trim()) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const reasonLines = doc.splitTextToSize(
            `Grund: ${invoice.details.reasonText.trim()}`,
            contentWidth,
        ) as string[];
        for (const l of reasonLines) {
            ensureSpace(25);
            doc.text(l, margin, y);
            y += 5;
        }
        y += 3;
    }

    // ── Einleitung ────────────────────────────────────────────────────────
    if (invoice.details?.introText?.trim()) {
        // Luft vor dem Anschreiben — zwei Zeilenhoehen des Kopfblocks (4.6 mm),
        // also 9.2 mm. Sie trennt den Belegkopf — Ueberschrift, Projekt,
        // Kostenstelle, Zusatztext — vom Brief; vorher standen beide Teile so
        // dicht beieinander, dass die erste Anschreibenzeile wie eine weitere
        // Kopfangabe wirkte. Ein erster Versuch mit 3 mm reichte dafuer nicht:
        // der Abstand blieb kleiner als der zwischen zwei Kopfzeilen und las
        // sich damit weiter als Fortsetzung des Kopfes.
        //
        // Hier und nicht am Ende der Kopfbloecke, weil die Trennung an ALLEN
        // Wegen dorthin haengt: mit und ohne Projektzeile, mit und ohne
        // Kostenstelle, Zusatztext oder Grundzeile — und gleichermassen fuer
        // ein Anschreiben, das mit einer Anrede beginnt, wie fuer eines, das
        // gleich mit dem Einleitungssatz einsetzt.
        y += INTRO_GAP;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(tp0, tp1, tp2);
        const introLines = doc.splitTextToSize(
            invoice.details.introText.trim(),
            contentWidth,
        ) as string[];
        for (const l of introLines) {
            ensureSpace(25);
            doc.text(l, margin, y);
            y += INTRO_LINE_HEIGHT;
        }
        y += 5;
    }

    // ── Positionstabelle ──────────────────────────────────────────────────
    // Abschnitte werden als fette Zeile ueber die volle Breite gerendert —
    // dieselbe didParseCell-Technik wie die NEP-Darstellung im Angebot. Bei
    // einer Sammelrechnung traegt jeder Abschnitt ein Projekt.
    // Der Andruck liest die DARGESTELLTEN Zeilen: bei Monatspauschale werden
    // gedeckte Positionen zu einer Summenzeile (presentInvoiceLines), die
    // Summen des Belegs bleiben dieselben.
    const visible = presentInvoiceLines(invoice).filter((l) => l.kind !== 'ADVANCE_DEDUCTION');

    const rowFor = (l: InvoiceLine): string[] => {
        if (l.kind === 'SECTION' || l.kind === 'TEXT') {
            return [l.posNumber || '', l.title || '', '', '', '', ''];
        }
        return [
            l.posNumber || '',
            // Der Nachlassvermerk steht IN der Leistungsspalte und nicht in
            // einer eigenen Spalte: die vier festen Zahlenspalten lassen der
            // Leistung 72 mm, ihre Untergrenze sind 60 mm — eine weitere
            // Betragsspalte passt nicht, ohne die Bezeichnung zur Textwueste zu
            // machen. Der Einzelpreis bleibt dafuer der vereinbarte Preis, und
            // der Vermerk nennt Grundlage und Abzug, sodass der Empfaenger das
            // Zeilennetto nachrechnen kann.
            //
            // Aus demselben Grund steht der Anteilsvermerk daneben: bei einem
            // geteilten Projekt traegt die Zeile die GETEILTE Menge, und ohne
            // den Vermerk sieht der Empfaenger eine Forderung ueber 4 h, waehrend
            // das Protokoll 8 h ausweist — er kann nicht nachrechnen, warum nur
            // ein Teil der erbrachten Menge gefordert wird, und muss nachfragen.
            // Der zweite Grund ist unserer: wir lesen eigene Belege spaeter
            // wieder EIN (Alt-Rechnungs-Import, Deckungspruefung). Steht der
            // Anteil nicht auf dem Papier, laesst sich der Teilbeleg nicht mehr
            // von einer Vollabrechnung unterscheiden, und die fehlende Haelfte
            // taucht als "erbracht, nie abgerechnet" wieder auf.
            [l.title, l.description, lineDiscountNote(l), splitShareNote(l)]
                .filter(Boolean)
                .join('\n'),
            // Menge und Einheit in EINER Spalte, wie in der Anwendung: "3 Stück".
            // Getrennt kostete die Einheit 16 mm Breite fuer ein Wort, das ohne
            // die Zahl daneben nichts aussagt.
            [l.quantity != null ? fmtQuantity(l.quantity) : '', l.unit || '']
                .filter(Boolean)
                .join(' '),
            l.unitPriceCents != null ? formatCents(l.unitPriceCents) : '',
            formatTaxRateBp(l.taxRateBp),
            formatCents(l.netCents),
        ];
    };

    // Zwischensumme je Abschnitt. Nur bei mehreren Abschnitten sinnvoll: bei
    // einem einzigen waere sie identisch mit dem Nettobetrag und damit nur
    // Rauschen. Bei der Sammelrechnung traegt jeder Abschnitt ein Projekt —
    // ohne Zwischensumme kann der Empfaenger den Beleg nicht auf seine
    // Kostenstellen aufteilen, ohne selbst zu addieren.
    const sectionCount = visible.filter((l) => l.kind === 'SECTION').length;
    const withSubtotals = sectionCount >= 2;


    const body: string[][] = [];
    // Parallel zu body: die Zeilenart je Tabellenzeile, gelesen von
    // didParseCell. Muss dieselbe Laenge wie body haben, sonst wird die
    // falsche Zeile formatiert.
    const kinds: Array<InvoiceLine['kind'] | 'SUBTOTAL'> = [];

    let openSection: { title: string; netCents: number; services: number } | null = null;
    const flushSubtotal = () => {
        if (!withSubtotals || !openSection || openSection.services === 0) return;
        body.push([
            '',
            `Zwischensumme ${openSection.title}`,
            '',
            '',
            '',
            formatCents(openSection.netCents),
        ]);
        kinds.push('SUBTOTAL');
    };

    for (const l of visible) {
        if (l.kind === 'SECTION') {
            flushSubtotal();
            openSection = { title: l.title || '', netCents: 0, services: 0 };
        } else if (l.generated) {
            // Belegweit erzeugte Zeilen (Nachlass, Nebenkostenzuschlag) haengen
            // an der GESAMTEN Honorarsumme und stehen deshalb hinter der letzten
            // Position — sie gehoeren zu keinem Abschnitt. Ohne diesen Schnitt
            // zoege der letzte Abschnitt sie in seine Zwischensumme und wiese
            // den Nachlass des ganzen Belegs als seinen eigenen aus.
            flushSubtotal();
            openSection = null;
        } else if (openSection && CONTRIBUTES_TO_NET.has(l.kind)) {
            // Zwischensumme ueber dieselben Arten, die zum Nettobetrag
            // beitragen — bis hierher zaehlte sie nur SERVICE, und ein
            // Nachlass minderte die Gesamtsumme, nicht die Zwischensumme.
            // Nachlass- und Zuschlagszeilen DIESES Abschnitts zaehlen mit: sonst
            // wiese die Zwischensumme mehr aus, als der Abschnitt kostet, und
            // die Summe der Zwischensummen ueberstiege den Nettobetrag.
            openSection.netCents += l.netCents;
            if (l.kind === 'SERVICE') openSection.services += 1;
        }
        body.push(rowFor(l));
        kinds.push(l.kind);
    }
    flushSubtotal();

    const sumColRight = rightX - 2;

    // Die Pos.-Spalte nach den tatsaechlich gedruckten Nummern bemessen. Die
    // Kopfzeile "Pos." bleibt aussen vor: sie ist schmaler als die
    // Mindestbreite und wuerde nichts aendern. Gemessen bei der Schrift der
    // Tabelle — die Zellen der Spalte stehen alle in normaler Staerke
    // (Abschnitts- und Zwischensummenzeilen tragen keine Nummer).
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(INVOICE_TABLE.fontSize);
    const posCol = invoicePosColumn(
        body.map((r) => r[0]),
        (t) => doc.getTextWidth(t),
    );

    autoTable(doc, {
        startY: y,
        head: [['Pos.', 'Leistung', 'Menge', 'EP', 'USt', 'Netto']],
        body,
        // 'plain' statt 'grid': das Gitter aus der Anwendung besteht nur aus
        // einer Linie unter jeder Zeile, nicht aus einem Kasten um jede Zelle.
        // Die Linien zeichnet didDrawCell unten selbst.
        theme: 'plain',
        showHead: 'everyPage',
        margin: lh.autoTableMargin(),
        // autoTable legt Seiten selbst an — ohne diesen Callback bliebe jede
        // Folgeseite ohne Briefpapier.
        willDrawPage: lh.willDrawPage,
        headStyles: {
            textColor: [ts0, ts1, ts2],
            fontSize: 8,
            fontStyle: 'bold',
            lineWidth: 0,
        },
        styles: {
            fontSize: INVOICE_TABLE.fontSize,
            textColor: [tp0, tp1, tp2],
            cellPadding: {
                top: 2.2,
                bottom: 2.2,
                left: INVOICE_TABLE.paddingX,
                right: INVOICE_TABLE.paddingX,
            },
            lineWidth: 0,
            // Nichts wird abgeschnitten: passt ein Inhalt nicht in seine Spalte,
            // bricht er um und die Zeile wird hoeher. Bei Betraegen und Mengen
            // waere ein verschluckter Rest eine falsche Zahl auf einer
            // festgeschriebenen Rechnung.
            overflow: 'linebreak',
        },
        columnStyles: {
            // fontSize NUR im Notfall mitgeben (Nummer passt selbst bei der
            // Kappung nicht): sonst uebersteuerte columnStyles auch die
            // kleinere Schrift der Kopfzeile, und jeder Beleg saehe anders aus.
            0: {
                cellWidth: posCol.width,
                ...(posCol.fontSize < INVOICE_TABLE.fontSize
                    ? { fontSize: posCol.fontSize }
                    : {}),
            },
            1: { cellWidth: 'auto' },
            2: { cellWidth: INVOICE_TABLE.widths.quantity, halign: 'right' },
            3: { cellWidth: INVOICE_TABLE.widths.unitPrice, halign: 'right' },
            4: { cellWidth: INVOICE_TABLE.widths.tax, halign: 'right' },
            5: { cellWidth: INVOICE_TABLE.widths.net, halign: 'right', fontStyle: 'bold' },
        },
        didParseCell: (data) => {
            // Kopfzeile der Zahlenspalten ebenfalls rechtsbuendig, sonst steht
            // die Beschriftung links ueber rechtsbuendigen Werten. Ausdruecklich
            // hier gesetzt statt sich auf columnStyles zu verlassen: didParseCell
            // hat in autoTable die letzte Entscheidung, und die Rangfolge
            // zwischen headStyles und columnStyles ist nicht offensichtlich.
            if (data.section === 'head') {
                if (data.column.index >= 2) data.cell.styles.halign = 'right';
                return;
            }
            if (data.section !== 'body') return;
            const kind = kinds[data.row.index];
            if (kind === 'SECTION') {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [248, 250, 252];
            } else if (kind === 'SUBTOTAL') {
                // Beschriftung rechtsbuendig direkt an den Betrag heran, damit
                // die Zwischensumme optisch zur Zahl gehoert und nicht zur
                // Leistungsspalte darueber.
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.halign = 'right';
            } else if (kind === 'TEXT') {
                data.cell.styles.fontStyle = 'italic';
                data.cell.styles.textColor = [ts0, ts1, ts2];
            }
        },
        didDrawCell: (data) => {
            // Genau EINE Linie je Zeile, gezeichnet an der letzten Spalte.
            // Zeichnete man sie an jeder Zelle, laege sie sechsfach uebereinander
            // — bei duennen Linien sichtbar als dickerer, dunklerer Strich.
            if (data.column.index !== data.table.columns.length - 1) return;
            const lineY = data.cell.y + data.cell.height;
            const isHead = data.section === 'head';
            doc.setDrawColor(ln0, ln1, ln2);
            // Unter dem Kopf kraeftiger: er trennt Beschriftung von Inhalt,
            // die uebrigen Linien trennen nur Zeilen voneinander.
            doc.setLineWidth(isHead ? 0.3 : 0.1);
            doc.line(margin, lineY, rightX, lineY);
        },
    });

    y = ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 6;

    // ── Summenblock ───────────────────────────────────────────────────────
    // Je Steuersatz eine eigene Zeile mit ihrer Bemessungsgrundlage. Eine
    // gesammelte Umsatzsteuerzeile waere bei mehreren Saetzen nicht §14-konform,
    // weil sich die enthaltene Steuer nicht mehr zuordnen liesse.
    const labelX = margin + 55;
    // `labelAt` rueckt die Beschriftung ein — fuer die "davon"-Zeilen des
    // Einbehalts, die Erlaeuterung sind und keine Summanden der Spalte.
    const sumRow = (
        label: string,
        value: string,
        o?: { bold?: boolean; size?: number; color?: number[]; labelAt?: number },
    ) => {
        ensureSpace(25);
        doc.setFont('helvetica', o?.bold ? 'bold' : 'normal');
        doc.setFontSize(o?.size ?? 10);
        const c = o?.color ?? COLORS.text.primary;
        doc.setTextColor(c[0], c[1], c[2]);
        doc.text(label, o?.labelAt ?? labelX, y);
        doc.text(value, sumColRight, y, { align: 'right' });
        y += o?.size && o.size > 10 ? 7 : 6;
    };

    const rule = (weight: number) => {
        doc.setDrawColor(tp0, tp1, tp2);
        doc.setLineWidth(weight);
        doc.line(labelX, y - 4.5, sumColRight, y - 4.5);
    };

    const t = invoice.totals;
    sumRow('Nettobetrag', formatCents(t.netTotalCents));
    for (const g of t.taxGroups) {
        if (g.taxRateBp === 0) {
            // Nicht jede Null ist eine Steuerbefreiung. Bei § 13b UStG ist der
            // Umsatz steuerpflichtig, nur schuldet der Empfaenger die Steuer —
            // "Steuerfrei" waere dort eine falsche steuerliche Aussage auf
            // einem Beleg, der beim Finanzamt landet.
            sumRow(
                `${ZERO_RATE_LABEL[g.taxCategoryCode] ?? 'Ohne Umsatzsteuer'} auf ${formatCents(g.taxableNetCents)}`,
                formatCents(0),
            );
        } else {
            // Ohne "auf <Betrag>": die Bemessungsgrundlage steht als
            // Nettobetrag direkt drueber (bzw. je Gruppe in den Positionen)
            // — die Wiederholung las sich wie eine doppelte Zeile.
            sumRow(`zzgl. ${formatTaxRateBp(g.taxRateBp)} USt`, formatCents(g.taxCents));
        }
    }
    rule(0.4);
    sumRow('Bruttobetrag', formatCents(t.grossTotalCents), { bold: true, size: 11 });

    // ── Sicherheitseinbehalt: Schalter und gemeinsame Helfer ──────────────
    // Der Einbehalt mindert AUSSCHLIESSLICH den Zahlbetrag (Plan §2): Netto,
    // Steuer und Brutto darueber bleiben vollstaendig ausgewiesen. Erst ein
    // Einbehalt, ein bereits einbehaltener Vorbetrag oder eine Freigabe
    // schaltet den erweiterten Summenblock ein — ohne alles bleibt der Beleg
    // ZEICHENGLEICH zu vorher, Altbelege tragen die Felder nicht und duerfen
    // sich im Andruck nicht aendern.
    //
    // Kumulatives Modell (Plan §16, computeRetention): der Beleg zeigt den
    // Einbehalt auf den LEISTUNGSSTAND, davon das bereits mit den Vorbelegen
    // Einbehaltene und das mit diesem Beleg Neue; der Abzugsblock zeigt die
    // tatsaechlich ERHALTENEN Zahlungen (Forderung des Vorbelegs minus dessen
    // Einbehalt). Die Spalte geht damit auf:
    //
    //   Bruttobetrag - kumulativer Einbehalt - erhaltene Zahlungen = Zahlbetrag
    //
    // Eine Freigabe steht NICHT als Summand in der Spalte: sie steckt bereits
    // darin, weil die erhaltenen Zahlungen um den Einbehalt kleiner sind als
    // die Forderungen. Sie wird deshalb als Erlaeuterung "im Zahlbetrag
    // enthalten" gedruckt, nie als Plus-Zeile — sonst zaehlte der Leser sie
    // doppelt.
    const retentionCents = t.retentionCents ?? 0;
    const retentionReleaseCents = t.retentionReleaseCents ?? 0;
    // Altbestand aus dem ersten Bau traegt nur retention/release; die
    // kumulativen Felder fehlen. Dort galt: was freigegeben wird, war vorher
    // einbehalten (prior = release), und was einbehalten wird, ist der ganze
    // Bestand (cumulative = retention). Aus der Spaltenidentitaet
    // Brutto - cumulative - (prepaid - prior) = Zahlbetrag geht der
    // eingefrorene Zahlbetrag damit weiterhin auf.
    const legacyRetention = t.cumulativeRetentionCents === undefined;
    const priorRetentionCents =
        t.priorRetentionCents ?? (legacyRetention ? retentionReleaseCents : 0);
    const cumulativeRetentionCents =
        t.cumulativeRetentionCents ?? priorRetentionCents + retentionCents - retentionReleaseCents;
    const paidPriorCents = t.paidPriorCents ?? t.prepaidCents - priorRetentionCents;
    const hasRetention =
        cumulativeRetentionCents !== 0 ||
        priorRetentionCents !== 0 ||
        retentionCents !== 0 ||
        retentionReleaseCents !== 0;
    // Bemessungsgrundlage ist der Leistungsstand (gross). Der Altbestand hat
    // seinen Einbehalt noch auf gross - prepaid gerechnet; dort muss die
    // gedruckte Grundlage die sein, aus der der eingefrorene Betrag entstand,
    // sonst behauptete der Beleg "5 % auf X" mit einer Zahl, die nicht 5 %
    // von X ist.
    const retentionBasisCents = legacyRetention
        ? invoiceAmountCents(t.grossTotalCents, t.prepaidCents)
        : t.grossTotalCents;
    const priorRetentions: PriorRetention[] = invoice.priorRetentions ?? [];

    // Das Vorzeichen kommt aus dem Betrag, nicht aus einem Praefix.
    // Die Null wird ausdruecklich abgefangen: -0 formatiert als "-0,00 EUR".
    const deductionAmount = (grossCents: number): string =>
        formatCents(grossCents === 0 ? 0 : -grossCents);

    // Eine Freigabe ERHOEHT den Zahlbetrag; das Plus steht ausdruecklich da,
    // sonst laese sich die Zeile zwischen den Minusbetraegen wie ein weiterer
    // Abzug. Negativ (Storno) traegt der Betrag sein eigenes Minus.
    const releaseAmount = (cents: number): string =>
        cents > 0 ? `+${formatCents(cents)}` : formatCents(cents);

    // Aufschluesselung unter einer Abzugszeile — dieselbe Form fuer
    // Einzelaufstellung und Zusammenfassung: "netto X · USt 19 % Y". Vorher
    // stand sie zusammengefasst IN der Beschriftung ("zu 19 % USt — netto
    // …, USt …") und las sich als anderer Beleg als die Einzelzeilen.
    // Weitere Zeilen (`more`) folgen enger gesetzt, wie die Aufteilung unter
    // dem Zahlbetrag — der Einbehalt-Hinweis passt nicht mehr in die erste
    // Zeile, sobald ein Zahlungseingang danebensteht.
    const breakdownLine = (text: string, more: string[] = []) => {
        ensureSpace(20 + more.length * 4);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(ts0, ts1, ts2);
        doc.text(text, labelX + 4, y);
        for (const l of more) {
            y += 4;
            doc.text(l, labelX + 4, y);
        }
        y += 6;
    };

    /**
     * Netto/USt-Aufteilung des Rechnungsbetrags (gross - prepaid) unter dem
     * Zahlbetrag. Ein einziger Satz kommt ohne Satzangabe je Zeile aus. Sobald
     * ein zweiter dazukommt — auch ein Nullsatz —, muss jede Zeile sagen, zu
     * welchem Satz ihr Netto gehoert; eine Summe ueber zwei Saetze waere nicht
     * mehr zuzuordnen. `prefix` ist "davon ", wenn die Teile den Zahlbetrag
     * ergeben, sonst leer (mit Einbehalt: die Bruecke folgt darunter).
     */
    const payableSplitLines = (
        remaining: ReturnType<typeof payableBreakdown>,
        prefix: string,
    ): string[] => {
        const onlyGroup = remaining.groups.length === 1 ? remaining.groups[0] : null;
        return onlyGroup && onlyGroup.taxRateBp !== 0
            ? [
                  `${prefix}netto ${formatCents(onlyGroup.netCents)} · USt ${formatTaxRateBp(onlyGroup.taxRateBp)} ${formatCents(onlyGroup.taxCents)}`,
              ]
            : remaining.groups.map((g) =>
                  g.taxRateBp === 0
                      ? `${prefix}ohne USt: netto ${formatCents(g.netCents)}`
                      : `${prefix}zu ${formatTaxRateBp(g.taxRateBp)} USt: netto ${formatCents(g.netCents)} · USt ${formatCents(g.taxCents)}`,
              );
    };

    // Nummerierung EINMAL rechnen: Ueberschrift, Einzelzeilen und
    // Summenzeile muessen dieselbe Spanne zeigen, sonst behauptet der Beleg
    // an drei Stellen dreierlei. Auch die Einbehalt-Zeilen lesen ihre
    // Nummern von hier, damit "Abschläge 1–6" oben dasselbe meint wie im
    // Abzugsblock darunter.
    const numbering = invoice.details?.advanceNumbering;
    const plan = advanceNumberingPlan(invoice.advanceDeductions, numbering);
    const ordinalBySource = new Map<string, number>();
    invoice.advanceDeductions.forEach((d, i) => {
        if (!ordinalBySource.has(d.advanceInvoiceId)) {
            ordinalBySource.set(d.advanceInvoiceId, plan.ordinals[i]);
        }
    });

    // Einbehalt je Quelle: der Abzugsblock zieht ihn von der Forderung des
    // Vorbelegs ab. Ein Vorbeleg ueber mehrere Steuersaetze hat mehrere
    // Abzugszeilen, aber EINEN Einbehalt — der wird auf der ERSTEN Zeile der
    // Quelle abgezogen und dort vermerkt (der Einbehalt hat keinen
    // Steueranteil, eine Verteilung auf die Saetze waere erfunden).
    const retentionBySource = new Map<string, number>();
    for (const r of priorRetentions) {
        retentionBySource.set(r.sourceId, (retentionBySource.get(r.sourceId) ?? 0) + r.retentionCents);
    }

    /**
     * Spanne der Vorbelege mit Einbehalt ("mit Abschlägen 1–6"), NUR wenn sie sich
     * sauber aus den gedruckten Abzugsnummern ableiten laesst: jeder Eintrag
     * findet seinen Vorbeleg im Abzugsblock, keine Nummer doppelt, keine
     * Luecke. Sonst null und die Zeile kommt ohne Spanne — eine geratene
     * Spanne waere eine falsche Aussage auf dem Beleg, eine fehlende nur eine
     * kuerzere. Folgt der Nummerierungsvorgabe am Beleg (plan), damit oben
     * dieselben Nummern stehen wie unten.
     */
    const priorRetentionSpanLabel = (list: PriorRetention[]): string | null => {
        if (plan.hidden || list.length === 0) return null;
        const ordinals: number[] = [];
        for (const r of list) {
            const o = ordinalBySource.get(r.sourceId);
            if (o === undefined) return null;
            ordinals.push(o);
        }
        const distinct = [...new Set(ordinals)].sort((a, b) => a - b);
        if (distinct.length !== list.length) return null;
        const from = distinct[0];
        const to = distinct[distinct.length - 1];
        if (to - from + 1 !== distinct.length) return null;
        // Im Dativ, weil die Zeile "bereits mit … einbehalten" heisst.
        return from === to ? `mit Abschlag ${from}` : `mit Abschlägen ${from}–${to}`;
    };

    /**
     * Einzelzeile eines Vorbeleg-Einbehalts: "aus 1. Abschlag AR-… vom …".
     * Die laufende Nummer kommt aus dem Abzugsblock; ohne Zuordnung (oder bei
     * ausgeschalteter Nummerierung) bleibt "aus Abschlag AR-…". Ein von Hand
     * nachgetragener Alt-Einbehalt (Backfill) wird als solcher markiert: der
     * Vorbeleg selbst traegt ihn nicht, der Leser soll ihn auf dem alten
     * Papier nicht suchen.
     */
    const priorRetentionLineLabel = (r: PriorRetention): string => {
        const o = plan.hidden ? undefined : ordinalBySource.get(r.sourceId);
        const beleg = `${r.invoiceNumber || 'ohne Nummer'} vom ${formatDate(r.invoiceDate)}`;
        const head = o === undefined ? `aus Abschlag ${beleg}` : `aus ${o}. Abschlag ${beleg}`;
        return r.backfilled ? `${head} (Alt-Einbehalt)` : head;
    };

    // ── Sicherheitseinbehalt ──────────────────────────────────────────────
    // VOR dem Abzugsblock, direkt unter dem Bruttobetrag: der Einbehalt
    // bemisst sich am Leistungsstand, den die Zeile darueber nennt, und der
    // Abzugsblock darunter zeigt bereits die um den Einbehalt gekuerzten
    // Zahlungen — die Reihenfolge ist die Reihenfolge der Rechnung.
    //
    // Zwei Formen: solange ein kumulativer Einbehalt besteht (Abschlag,
    // Nachholung, Satzwechsel, deren Storno), die Einbehaltzeile mit ihren
    // "davon"-Unterzeilen; ist er 0 und wird freigegeben (Schlussrechnung,
    // deren Storno), der Freigabeblock.
    if (cumulativeRetentionCents !== 0 || retentionCents !== 0) {
        // Im Storno sind die Werte gespiegelt negativ: dann wird nicht
        // einbehalten, sondern zurueckbelastet, und "abzueglich" waere
        // sachlich falsch — wie beim Abzugsblock.
        const reversal = cumulativeRetentionCents < 0 || retentionCents < 0;
        // Satz aus derselben Quelle wie die Berechnung (effektiver Satz; der
        // Storno kopiert ihn vom Original). Ohne hinterlegten Satz (Altdaten)
        // bleibt die Zeile lesbar, nur ohne Prozentangabe. Grossgeschrieben,
        // weil die Zeile hier eine Summenzeile im Rang des Abzugsblock-Titels
        // ist ("Abzüglich erhaltener Abschlagszahlungen").
        const percent = effectiveRetentionPercent(invoice.documentType, invoice.details);
        const note = percent > 0 ? retentionNote(percent) : 'abzüglich Sicherheitseinbehalt';
        const verb = reversal ? 'Rückbelastung' : 'Abzüglich';
        const label = `${verb}${note.replace(/^abzüglich/, '')} auf ${formatCents(retentionBasisCents)}`;
        ensureSpace(40);
        // Dieselbe Vorzeichenregel wie die Abzuege: positiv gespeichert,
        // negativ gedruckt. Im Rang der Summenzeile des Abzugsblocks (fett
        // 9,5): beide sind Summanden der Spalte.
        sumRow(label, deductionAmount(cumulativeRetentionCents), { bold: true, size: 9.5 });

        // Die "davon"-Zeilen sind Erlaeuterung, keine Summanden: eingerueckt,
        // kleiner, Betraege ohne Vorzeichenregel (im Storno gespiegelt
        // negativ). Nur wenn etwas aufzuteilen ist — bei prior 0 (Nachholung,
        // erster Abschlag) waere "davon mit dieser Rechnung" die Wiederholung
        // der Zeile darueber.
        if (priorRetentionCents !== 0) {
            const source = priorRetentionSpanLabel(priorRetentions) ?? 'mit früheren Rechnungen';
            // "davon" nur, wenn das Vorherige im kumulativen Betrag enthalten
            // ist. Bei einer Freigabe uebersteigt es ihn — dann ist es kein
            // Teil davon, sondern mehr.
            const davon = retentionReleaseCents !== 0 ? '' : 'davon ';
            sumRow(`${davon}bereits ${source} einbehalten`, formatCents(priorRetentionCents), {
                size: 9,
                labelAt: labelX + 4,
            });
            if (retentionCents !== 0) {
                sumRow('davon mit dieser Rechnung', formatCents(retentionCents), {
                    size: 9,
                    labelAt: labelX + 4,
                });
            }
        }
        if (retentionReleaseCents !== 0) {
            // Satzwechsel nach unten: das Vorherige uebersteigt den neuen
            // kumulativen Einbehalt, die Differenz wird frei. Sie ist im
            // Zahlbetrag enthalten (die Zahlungen im Abzugsblock sind um das
            // volle Vorherige gekuerzt) und deshalb kein Summand der Spalte.
            sumRow(
                'Freigabe der Differenz mit dieser Rechnung (im Zahlbetrag enthalten)',
                releaseAmount(retentionReleaseCents),
                { size: 9, labelAt: labelX + 4 },
            );
        }
    } else if (retentionReleaseCents !== 0) {
        // Schlussrechnung (Satz 0): alles bisher Einbehaltene wird frei. Der
        // Block hat den Aufbau des Abzugsblocks — Titel am Rand, Einzelzeilen
        // je Vorbeleg, Summe bei mehreren — und traegt seinen Betrag NICHT
        // in der Spalte: die Freigabe steckt im Zahlbetrag, weil die
        // erhaltenen Zahlungen darunter um genau sie kleiner sind als die
        // Forderungen. Als Plus-Zeile zaehlte der Leser sie doppelt.
        const reversal = retentionReleaseCents < 0;
        y += 4;
        ensureSpace(40);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(
            reversal
                ? 'Sicherheitseinbehalt: Rückbelastung der freigegebenen Beträge (im Zahlbetrag enthalten):'
                : 'Sicherheitseinbehalt: Freigabe der bisher einbehaltenen Beträge (im Zahlbetrag enthalten):',
            margin,
            y,
        );
        y += 6;
        for (const r of priorRetentions) {
            sumRow(priorRetentionLineLabel(r), releaseAmount(r.retentionCents), { size: 9 });
        }
        if (priorRetentions.length !== 1) {
            // Summenzeile bei mehreren Belegen — bei einem waere sie die
            // Wiederholung der Zeile darueber (Muster advanceDeductionRangeLabel).
            // Ohne Liste (eingefrorene Summe, Altdaten) ist sie die einzige
            // Zeile: sonst fehlte dem Zahlbetrag die Herleitung.
            if (priorRetentions.length > 1) {
                y += 1;
                rule(0.1);
            }
            sumRow(
                reversal ? 'Rückbelastung gesamt' : 'Freigabe gesamt',
                releaseAmount(retentionReleaseCents),
                { bold: true, size: 9.5 },
            );
        }
        breakdownLine(
            'Einbehalte ohne Umsatzsteueranteil; die Steuer wurde mit den Abschlägen ausgewiesen.',
        );
    }

    // ── Abschlagsabzug ────────────────────────────────────────────────────
    if (invoice.advanceDeductions.length > 0) {
        y += 4;
        ensureSpace(40);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(tp0, tp1, tp2);
        // Beim Storno und bei Korrekturen sind die Abzuege selbst negativ: der
        // Abschlag wird nicht abgezogen, sondern zurueckbelastet. Eine
        // Ueberschrift "Abzueglich" waere dann sachlich falsch, und ein hart
        // vorangestelltes Minus ergaebe "- -2.380,00 EUR".
        const deductionsAreReversal = invoice.advanceDeductions.every((d) => d.grossCents <= 0);
        // Ueberschrift und Spanne kommen aus advanceInvoiceLogic — dieselbe
        // Quelle, aus der die Maske ihre Vorgabe zieht. Am Beleg eingegebener
        // Text (details.advanceDeductionHeading) verdraengt sie vollstaendig.
        doc.text(
            `${advanceDeductionHeadingLabel(invoice.advanceDeductions, {
                mode: invoice.advanceDeductionMode,
                override: invoice.details?.advanceDeductionHeading,
                numbering,
            })}:`,
            margin,
            y,
        );
        y += 6;

        // Mit Einbehalt zeigt jede Zeile die tatsaechlich ERHALTENE Zahlung
        // (Forderung minus Einbehalt der Quelle) und vermerkt die Kuerzung
        // darunter — die Zahl, die der Auftraggeber in seinen Ueberweisungen
        // wiederfindet (Befund der Buchhaltung, Plan §16). Ohne Einbehalt
        // bleibt alles wie bisher.
        // Betraege ohne Vorzeichen: der Vermerk beschreibt den Vorbeleg, und
        // der ist auch im Storno (gespiegelte Abzuege) derselbe.
        const retentionRemark = (claimCents: number, retainedCents: number): string[] =>
            retainedCents === 0
                ? []
                : [
                      `Forderung ${formatCents(Math.abs(claimCents))} abzgl. ${formatCents(Math.abs(retainedCents))} Sicherheitseinbehalt`,
                  ];

        if (invoice.advanceDeductionMode === 'GROUPED') {
            // Auch "zusammengefasst" heisst je Steuersatz eine Zeile. Bei einem
            // einzigen Satz traegt die Zeile den Sammelbegriff; erst ab zwei
            // Saetzen muss die Beschriftung sagen, zu welchem Satz sie gehoert.
            const groups = groupAdvanceDeductions(invoice.advanceDeductions);
            const groupLabel = (taxRateBp: number): string => {
                const base = deductionsAreReversal
                    ? 'Rückbelastung Abschlagszahlungen'
                    : 'Abschlagszahlungen zusammengefasst';
                return groups.length > 1 ? `${base} zu ${formatTaxRateBp(taxRateBp)} USt` : base;
            };
            // Zusammengefasst gibt es keine Zeile je Quelle; der gesamte
            // Vorbeleg-Einbehalt wird auf der ERSTEN Gruppe abgezogen — dieselbe
            // Regel wie bei einem Vorbeleg ueber mehrere Steuersaetze in der
            // Einzelaufstellung.
            groups.forEach((g, i) => {
                const retained = i === 0 ? priorRetentionCents : 0;
                sumRow(groupLabel(g.taxRateBp), deductionAmount(g.grossCents - retained), {
                    size: 9,
                });
                breakdownLine(
                    `netto ${formatCents(g.netCents)} · USt ${formatTaxRateBp(g.taxRateBp)} ${formatCents(g.taxCents)}`,
                    retentionRemark(g.grossCents, retained),
                );
            });
            const refs = groups.flatMap((g) => g.sourceInvoiceNumbers);
            if (refs.length) {
                ensureSpace(20);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(ts0, ts1, ts2);
                const refLines = doc.splitTextToSize(
                    `Berücksichtigt wurden: ${refs.join(', ')}.`,
                    contentWidth - 55,
                ) as string[];
                for (const l of refLines) {
                    doc.text(l, labelX, y);
                    y += 4;
                }
                y += 2;
            }
        } else {
            // Jede Zeile traegt die laufende Nummer ihres Abschlags. Erst
            // dadurch ist die Spanne der Summenzeile ("Abschläge 1–6 gesamt")
            // am Beleg selbst nachpruefbar. Ein Abschlag ueber zwei
            // Steuersaetze steht mit zwei Zeilen da und traegt in beiden
            // dieselbe Nummer — die Wiederholung ist das Signal, dass es
            // derselbe Beleg ist, waehrend eine unnummerierte zweite Zeile wie
            // ein Beleg ohne Zugehoerigkeit aussaehe.
            const retentionTaken = new Set<string>();
            invoice.advanceDeductions.forEach((d, i) => {
                // Einbehalt der Quelle nur auf ihrer ersten Zeile abziehen.
                let retained = 0;
                if (!retentionTaken.has(d.advanceInvoiceId)) {
                    retentionTaken.add(d.advanceInvoiceId);
                    retained = retentionBySource.get(d.advanceInvoiceId) ?? 0;
                }
                sumRow(
                    advanceDeductionLineLabel(d, plan.ordinals[i], formatDate(d.invoiceDate), {
                        hidden: plan.hidden,
                    }),
                    deductionAmount(d.grossCents - retained),
                    { size: 9 },
                );
                // Zahlungseingang steht hier unten und nicht mehr in der
                // Kopfzeile: mit der vorangestellten Nummer wuerde die Kopfzeile
                // sonst bis in die Betragsspalte laufen. Die Aufschluesselung
                // hat bei 8 pt Platz dafuer.
                const paid = d.paymentDate
                    ? ` · Zahlungseingang ${formatDate(d.paymentDate)}`
                    : '';
                // Eigene Zeilenhoehe (breakdownLine). Vorher wurde die
                // Aufschluesselung auf y-2 gesetzt und y nur um 2 mm erhoeht —
                // der Trennstrich vor dem Zahlbetrag (rule zeichnet auf y-4.5)
                // lief dann quer durch die letzte Aufschluesselungszeile.
                breakdownLine(
                    `netto ${formatCents(d.netCents)} · USt ${formatTaxRateBp(d.taxRateBp)} ${formatCents(d.taxCents)}${paid}`,
                    retentionRemark(d.grossCents, retained),
                );
            });

            // Einzeln UND zusammengerechnet: unter der Aufstellung steht, was
            // insgesamt abgezogen wird. Die Beschriftung entscheidet zugleich,
            // OB die Zeile erscheint — bei nur einem Abschlag gibt sie null
            // zurueck, denn dann waere sie die Wiederholung der Zeile darueber.
            //
            // Bewusst EINE Bruttosumme ohne Steuerangabe, auch bei mehreren
            // Saetzen: eine gesammelte Steuerzeile waere nicht §14-UStG-konform.
            // Die Aufschluesselung leisten die Einzelzeilen und der Steuerblock,
            // diese Zeile ist nachrichtlich.
            const rangeLabel = advanceDeductionRangeLabel(invoice.advanceDeductions, {
                reversal: deductionsAreReversal,
                numbering,
            });
            if (rangeLabel) {
                // Etwas Luft, damit die duenne Linie nicht an der
                // Aufschluesselungszeile darueber klebt.
                y += 1;
                rule(0.1);
                sumRow(
                    rangeLabel,
                    // Mit Einbehalt die Summe der erhaltenen Zahlungen
                    // (totals.paidPriorCents), sonst derselbe Betrag wie
                    // totals.prepaidCents — dieselbe Vorzeichenregel wie in
                    // den Einzelzeilen.
                    deductionAmount(
                        hasRetention ? paidPriorCents : advanceDeductionTotal(invoice.advanceDeductions),
                    ),
                    { bold: true, size: 9.5 },
                );
            }
        }

        if (!hasRetention) {
            rule(0.4);
            // "(brutto)" gehoert in die Beschriftung: der Zahlbetrag entsteht aus
            // BRUTTO minus BRUTTO, waehrend die Zeilen darueber ("Nettobetrag",
            // "zzgl. USt") von der Gesamtleistung sprechen. Ohne den Zusatz wurde
            // die Zahl im Zahlungsverkehr schon als Nettoforderung gelesen.
            sumRow('Verbleibender Zahlbetrag (brutto)', formatCents(t.payableCents), {
                bold: true,
                size: 11,
            });

            // Aufschluesselung des Restbetrags in Entgelt und Umsatzsteuer. Rein
            // nachrichtlich — der Steuerausweis nach §14 UStG bleibt der Block ueber
            // der GESAMTLEISTUNG darueber. Gedruckt wird nur, wenn netto und Steuer
            // den Zahlbetrag auch ergeben; sonst stuenden hier zwei Zahlen, die
            // nicht zur fetten Zeile darueber passen.
            //
            // Mit Sicherheitseinbehalt steht sie unten beim Zahlbetrag, dort
            // mit der Bruecke vom Rechnungsbetrag (gross - prepaid) zum
            // Zahlbetrag.
            const remaining = payableBreakdown(t, invoice.advanceDeductions);
            if (remaining.consistent) {
                const splitLines = payableSplitLines(remaining, 'davon ');
                ensureSpace(20);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(ts0, ts1, ts2);
                for (const l of splitLines) {
                    doc.text(l, labelX + 4, y);
                    y += 4;
                }
                y += 2;
            }
        }
    }

    // ── Zahlbetrag mit Einbehalt ──────────────────────────────────────────
    // Ohne Einbehalt hat der Abzugsblock den Zahlbetrag bereits gedruckt
    // (bzw. ohne Abzuege ist der Bruttobetrag der Zahlbetrag) — zeichengleich
    // zu vorher. Mit Einbehalt schliesst die Spalte hier:
    // Bruttobetrag - Einbehalt - erhaltene Zahlungen = Zahlbetrag.
    //
    // Beschriftung und Aufschluesselung wie ohne Einbehalt (Betreiber
    // 19.10.2026: Belege mit und ohne Einbehalt sehen am Ende gleich aus).
    // Die Aufteilung "netto … · USt …" bleibt auf dem Rechnungsbetrag VOR
    // Einbehalt (gross - prepaid, payableBreakdown): der Einbehalt hat keinen
    // Steueranteil, eine Aufteilung des Zahlbetrags waere erfunden (Bauplan
    // §7.5). Deshalb ohne "davon" und mit der Bruecke zur fetten Zeile
    // darunter — "Forderung … abzgl. … Sicherheitseinbehalt", dasselbe Muster
    // wie die Abzugszeilen darueber. So stehen die Teile nie neben einer
    // Summe, die sie nicht ergeben. Der Steuerausweis nach § 14 UStG bleibt
    // der Steuerblock ueber der Gesamtleistung.
    if (hasRetention) {
        // Erst die Erlaeuterungszeilen bestimmen, dann drucken: der Platz fuer
        // Strich, Zahlbetrag UND Erlaeuterung wird in einem Stueck reserviert.
        // Sonst stuende die Erlaeuterung bei knappem Seitenrest allein auf der
        // Folgeseite, getrennt von dem Betrag, den sie erklaert.
        const notes: string[] = [];

        // Ohne Abzuege ist der Rechnungsbetrag der Bruttobetrag; Netto und
        // Steuer stehen dann unmittelbar darueber, die Aufteilung waere ihre
        // Wiederholung.
        const remaining = payableBreakdown(t, invoice.advanceDeductions);
        if (
            invoice.advanceDeductions.length > 0 &&
            remaining.consistent &&
            // Gedruckt wird nur, was aufgeht — sonst stuenden Teile da, die
            // die fette Zeile nicht ergeben (eingefrorene Altdaten).
            remaining.grossCents - retentionCents + retentionReleaseCents === t.payableCents
        ) {
            // Storno: Einbehalt und Freigabe gespiegelt negativ. Wie in der
            // Einbehaltzeile oben wird dann zurueckbelastet; ein Betrag mit
            // hartem Minus hinter "abzgl." ergaebe "abzgl. -219,76 €".
            const terms: string[] = [];
            if (retentionCents > 0) {
                terms.push(`abzgl. ${formatCents(retentionCents)} Sicherheitseinbehalt`);
            } else if (retentionCents < 0) {
                terms.push(`zzgl. ${formatCents(-retentionCents)} Rückbelastung Sicherheitseinbehalt`);
            }
            if (retentionReleaseCents > 0) {
                terms.push(`zzgl. ${formatCents(retentionReleaseCents)} Freigabe Sicherheitseinbehalt`);
            } else if (retentionReleaseCents < 0) {
                terms.push(`abzgl. ${formatCents(-retentionReleaseCents)} Rückbelastung Freigabe`);
            }
            // Ohne Einbehalt und Freigabe auf diesem Beleg (kumulativ bereits
            // erreicht) ist der Zahlbetrag der Rechnungsbetrag: dann genau wie
            // ohne Einbehalt, mit "davon".
            // Ein Rechnungsbetrag von 0 (Schlussrechnung, alles mit Abschlaegen
            // gefordert) hat nichts aufzuteilen; die Bruecke allein erklaert den
            // Zahlbetrag.
            const nothingToSplit = remaining.groups.every((g) => g.netCents === 0 && g.taxCents === 0);
            const split =
                terms.length > 0 && nothingToSplit
                    ? []
                    : payableSplitLines(remaining, terms.length === 0 ? 'davon ' : '');
            // In der Schrift messen, in der breakdownLine druckt — die letzte
            // Summenzeile stand in 11 pt fett, damit umbraeche die Bruecke zu
            // frueh.
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const bridge =
                terms.length === 0
                    ? []
                    : (doc.splitTextToSize(
                          `Forderung ${formatCents(remaining.grossCents)} ${terms.join(' ')}`,
                          sumColRight - labelX - 4,
                      ) as string[]);
            notes.push(...split, ...bridge);
        }

        // Strich und Zahlbetrag zusammenhalten: rule zeichnet auf y-4.5 VOR
        // der Zeile, ein Umbruch erst in sumRow liesse den Strich allein auf
        // der Vorseite zurueck. Mit Erlaeuterung: sumRow (11 pt) rueckt 7 mm
        // vor, breakdownLine verlangt danach 20 + 4 je Folgezeile.
        ensureSpace(notes.length > 0 ? 23 + 4 * notes.length : 25);
        rule(0.4);
        sumRow('Verbleibender Zahlbetrag (brutto)', formatCents(t.payableCents), {
            bold: true,
            size: 11,
        });
        const [first, ...more] = notes;
        if (first) breakdownLine(first, more);
    }

    // Doppelter Abschlussstrich unter dem Zahlbetrag — kaufmaennische Konvention.
    doc.setDrawColor(tp0, tp1, tp2);
    doc.setLineWidth(0.4);
    doc.line(labelX, y - 4.5, sumColRight, y - 4.5);
    doc.setLineWidth(0.2);
    doc.line(labelX, y - 3.5, sumColRight, y - 3.5);
    y += 6;

    // ── Zahlungsbedingungen und Bankverbindung ────────────────────────────
    const bank = invoice.details?.bankAccount;
    const iban = bank?.iban || config.iban;
    const bic = bank?.bic || config.bic;
    const bankName = bank?.bankName || config.bankName;

    // Ein negativer Zahlbetrag ist keine Forderung, sondern eine Erstattung.
    // Der konfigurierte Zahlungstext ("Bitte ueberweisen Sie …") stuende dort
    // als Zahlungsaufforderung ueber einem Minusbetrag — auf Storno,
    // Korrekturminderung und Gutschrift gleichermassen falsch.
    const isRefund = t.payableCents < 0;

    ensureSpace(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(isRefund ? 'Erstattung' : 'Zahlungsbedingungen', margin, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    // Ergaenzungssaetze zum Sicherheitseinbehalt haengen NUR am erzeugten
    // Standardtext. Frei eingegebene Zahlungsbedingungen (details.paymentTerms)
    // schreibt der Betreiber selbst und vollstaendig — ein automatisch
    // angehaengter Satz stuende dort womoeglich im Widerspruch zu seinem Text.
    // "ohne Abzug" bleibt: der Einbehalt ist im Zahlbetrag bereits abgezogen.
    //
    // Genannt wird der GESAMTE Einbehalt (kumulativ), nicht nur der Teil
    // dieses Belegs: faellig mit der Schlussrechnung ist alles, was bis dahin
    // einbehalten wurde. Beim Satzwechsel nach unten bleibt der Rest stehen
    // und wird ebenso genannt; die freigegebene Differenz nennt der Beleg im
    // Summenblock.
    const retentionSentences: string[] = [];
    if (cumulativeRetentionCents > 0 && retentionReleaseCents === 0) {
        retentionSentences.push(
            `Der Sicherheitseinbehalt von insgesamt ${formatCents(cumulativeRetentionCents)} wird mit der Schlussrechnung fällig.`,
        );
    }
    if (retentionReleaseCents > 0) {
        retentionSentences.push(
            invoice.documentType === 'FINAL_INVOICE'
                ? `Mit dieser Schlussrechnung sind die Sicherheitseinbehalte in Höhe von ${formatCents(retentionReleaseCents)} fällig.`
                : `Mit dieser Rechnung werden Sicherheitseinbehalte in Höhe von ${formatCents(retentionReleaseCents)} freigegeben.`,
        );
        if (cumulativeRetentionCents > 0) {
            retentionSentences.push(
                `Der verbleibende Sicherheitseinbehalt von insgesamt ${formatCents(cumulativeRetentionCents)} wird mit der Schlussrechnung fällig.`,
            );
        }
    }
    const standardTerms = [
        invoice.dueDate
            ? `Bitte überweisen Sie den Rechnungsbetrag ohne Abzug bis zum ${formatDate(invoice.dueDate)} unter Angabe der Rechnungsnummer.`
            : '',
        ...retentionSentences,
    ]
        .filter(Boolean)
        .join(' ');
    const terms = isRefund
        ? `Der Betrag von ${formatCents(-t.payableCents)} wird Ihnen erstattet oder mit der nächsten Rechnung verrechnet.`
        : invoice.details?.paymentTerms?.trim() || standardTerms;
    if (terms) {
        for (const l of doc.splitTextToSize(terms, contentWidth) as string[]) {
            ensureSpace(20);
            doc.text(l, margin, y);
            y += 4.5;
        }
    }
    // Skonto auf einen Erstattungsbetrag ergibt keinen Sinn. Ausserdem nicht
    // erneut ausgeben, wenn die Zahlungsbedingungen den Skonto-Satz bereits
    // als Text enthalten (kombinierter Satz aus details.paymentTerms) –
    // sonst stuende der Skonto-Hinweis doppelt im Beleg.
    if (
        invoice.details?.skonto &&
        invoice.details.skonto.percent > 0 &&
        invoice.details.skonto.days > 0 &&
        !isRefund &&
        !/skonto/i.test(terms)
    ) {
        ensureSpace(20);
        doc.text(
            `Bei Zahlung innerhalb von ${invoice.details.skonto.days} Tagen gewähren wir ${invoice.details.skonto.percent} % Skonto.`,
            margin,
            y,
        );
        y += 4.5;
    }
    // Auf einem Erstattungsbeleg hat die eigene Bankverbindung nichts zu
    // suchen: sie liest sich als Zahlungsaufforderung. Gebraucht wuerde dort
    // die Verbindung des Kunden, und die kennt der Beleg nicht.
    if (iban && !isRefund) {
        ensureSpace(20);
        y += 1;
        // Ausdruecklich hinterlegter Kontoinhaber (Beleg → Snapshot → Konfig).
        // Der Firmenname ist hier ABSICHTLICH kein Rueckfall: er steht im
        // Briefkopf, und die Zeile soll nur erscheinen, wenn die Bank eine
        // andere Schreibweise fuehrt.
        const holder =
            bank?.holder?.trim() ||
            invoice.companySnapshot?.accountHolder?.trim() ||
            config.accountHolder?.trim();
        if (holder) {
            for (const l of doc.splitTextToSize(
                `Zahlungsempfänger ${holder}`,
                contentWidth,
            ) as string[]) {
                doc.text(l, margin, y);
                y += 4.5;
            }
        }
        doc.text(
            [bankName, `IBAN ${iban}`, bic ? `BIC ${bic}` : ''].filter(Boolean).join(' · '),
            margin,
            y,
        );
        y += 4.5;
    }
    // Zusatztext unter den Kontodaten (Verwendungszweck, Skonto o. ae.) —
    // wie die Bankverbindung nicht auf Erstattungsbelegen.
    if (config.paymentNoteText?.trim() && !isRefund) {
        y += 1;
        for (const l of doc.splitTextToSize(
            config.paymentNoteText.trim(),
            contentWidth,
        ) as string[]) {
            ensureSpace(20);
            doc.text(l, margin, y);
            y += 4.5;
        }
    }

    // ── Pflichthinweise ───────────────────────────────────────────────────
    // Steuerbefreiung und Reverse Charge muessen im Klartext auf dem Beleg
    // stehen, nicht nur als Steuersatz 0 in der Tabelle.
    const notes: string[] = [];
    for (const g of t.taxGroups) {
        if (g.taxCategoryCode === 'AE') {
            notes.push('Steuerschuldnerschaft des Leistungsempfängers');
        } else if (g.taxRateBp === 0 && g.taxExemptionReason?.trim()) {
            notes.push(g.taxExemptionReason.trim());
        }
    }
    if (config.smallBusiness && config.smallBusinessNote?.trim()) {
        notes.push(config.smallBusinessNote.trim());
    }
    if (notes.length) {
        y += 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        for (const n of [...new Set(notes)]) {
            ensureSpace(20);
            doc.text(n, margin, y);
            y += 4.5;
        }
        doc.setFont('helvetica', 'normal');
    }

    // ── Schlusstext und Gruss ─────────────────────────────────────────────
    if (invoice.details?.outroText?.trim()) {
        y += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(tp0, tp1, tp2);
        for (const l of doc.splitTextToSize(
            invoice.details.outroText.trim(),
            contentWidth,
        ) as string[]) {
            ensureSpace(25);
            doc.text(l, margin, y);
            y += 5;
        }
    }

    // ── Schlussblock: Grussformel, Unterschrift, Name, Firmenname ─────────
    //
    // `details.hideClosing` laesst ihn fuer DIESEN Beleg ganz weg. Das ist der
    // Grund fuer die Klammer um den gesamten Abschnitt und nicht fuer vier
    // einzelne Bedingungen: `ensureSpace` unten haengt mit drin, und es legt
    // eine neue Seite an, wenn der Block nicht mehr aufs Blatt passt. Stuende
    // es ausserhalb, entstuende bei einer Rechnung, die knapp bis ans
    // Seitenende reicht, eine zweite Seite fuer einen Block, der gar nicht
    // gedruckt wird — ein leeres Blatt mit Fusszeile beim Kunden.
    //
    // Die Grussformel aus der Konfiguration wirkt INNERHALB des Blocks: leer
    // gepflegt heisst dort "ohne GRUSSZEILE" und sonst nichts. Bis 2026-08-24
    // nahm ein geleertes Feld den ganzen Block mit — wer nur die Anrede
    // weglassen wollte, verlor unbemerkt die Zeichnung des Belegs, und
    // dieselbe Pflegestelle steuert auch Angebot und Gutschrift.
    const hideClosing = invoice.details?.hideClosing === true;
    if (!hideClosing) {
        const closing = (config.closingText ?? 'Mit freundlichem Gruß').trim();
        y += 6;
        ensureSpace(35);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(tp0, tp1, tp2);
        if (closing) {
            doc.text(closing, margin, y);
            y += 4;
        }

        if (input.userSignature) {
            try {
                const props = doc.getImageProperties(input.userSignature);
                const sigH = 14;
                const sigW = Math.min(50, (props.width * sigH) / props.height);
                ensureSpace(45);
                doc.addImage(
                    input.userSignature,
                    lh.imgFmt(input.userSignature),
                    margin,
                    y,
                    sigW,
                    sigH,
                );
                y += sigH + 2;
            } catch (e) {
                console.warn('Rechnungs-PDF: Unterschrift konnte nicht eingebettet werden', e);
                y += 6;
            }
        } else {
            // Der Platz, den eine fehlende Unterschrift freihaelt.
            y += 6;
        }

        if (input.userName?.trim()) {
            doc.setFont('helvetica', 'bold');
            doc.text(input.userName.trim(), margin, y);
            y += 5;
        }
        if (company.name) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(ts0, ts1, ts2);
            doc.text(company.name, margin, y);
        }
    }

    // Die SEITENFUSSZEILE bleibt in jedem Fall: Anschrift, Registergericht,
    // Steuernummer und Bankverbindung sind Pflichtangaben und gehoeren nicht
    // zur Zeichnung des Belegs.
    drawFooters(doc, company, lh, geo);
    return doc;
};

/** Bequemer Adapter fuer invoiceService.IssueContext.renderPdf. */
export const renderInvoicePdfBytes = async (
    input: InvoicePdfInput,
    /**
     * Anlage, die hinten angehaengt wird — heute der Leistungsnachweis.
     *
     * Sie gehoert in DIESE Funktion und nicht in den Aufrufer, weil hier die
     * archivierten Bytes entstehen: der Hash muss ueber das vollstaendige
     * Dokument laufen, das der Kunde bekommt. Wer die Anlage spaeter anklebt,
     * archiviert eine Datei, die ihrem eigenen sha256 widerspricht.
     */
    annex?: Uint8Array | null,
): Promise<Uint8Array> => {
    // Erst laden, dann rendern: registerEmbeddedFont ist synchron, weil es
    // mitten im Zeichnen aufgerufen wird. Dieser Pfad erzeugt die archivierten
    // Bytes und ist damit der, bei dem die Einbettung wirklich zaehlt.
    await ensureEmbeddedFont();
    const bytes = new Uint8Array(generateInvoicePDF(input).output('arraybuffer'));
    return annex?.length ? appendPdf(bytes, annex) : bytes;
};
